import { useQuery } from "@tanstack/react-query";
import { getMe, listAuditLogs, listUsers } from "@/lib/api";

export function AuditPage() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const isAdmin = me?.role === "admin";

  return (
    <div>
      <header className="mb-8">
        <h1 className="page-title">Audit log</h1>
        <p className="mt-2 text-text-secondary">
          A record of who changed what across the portal.
        </p>
      </header>

      {me &&
        (isAdmin ? (
          <AuditTable />
        ) : (
          <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
            The audit log is only visible to admins.
          </p>
        ))}
    </div>
  );
}

function AuditTable() {
  const { data: entries, isLoading } = useQuery({
    queryKey: ["audit"],
    queryFn: () => listAuditLogs(200),
  });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const nameOf = (id: string | null) =>
    (id && users?.find((u) => u.id === id)?.name) || "—";

  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-lg" />
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
        <div className="max-w-sm">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
            <i className="fa-solid fa-list-check" />
          </div>
          <h2 className="font-display text-lg font-semibold text-text-heading">
            Nothing recorded yet
          </h2>
          <p className="mx-auto mt-1.5 text-sm text-text-secondary">
            Actions will appear here as portal modules start recording them — finance
            entries first, since that's where an audit trail matters most.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
            <th className="px-4 py-2.5 font-semibold">When</th>
            <th className="px-4 py-2.5 font-semibold">Who</th>
            <th className="px-4 py-2.5 font-semibold">Action</th>
            <th className="px-4 py-2.5 font-semibold">Entity</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-b border-border-light">
              <td className="whitespace-nowrap px-4 py-2.5 text-text-muted">
                {new Date(e.createdAt).toLocaleString()}
              </td>
              <td className="px-4 py-2.5 text-text-heading">{nameOf(e.actorId)}</td>
              <td className="px-4 py-2.5">
                <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text">
                  {e.action}
                </code>
              </td>
              <td className="px-4 py-2.5 text-text-secondary">
                {e.entityType}
                {e.entityId ? ` · ${e.entityId.slice(0, 8)}` : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
